import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import syncApi from 'ecommerce/api/sync';
import useModalContext from 'components/context/modal';
import uuid from 'utils/uuid';
import uniq from 'utils/uniq';

import DraftList from './components/draftList';

import useEditorContext from '../context/editorContext';
import useEditorRouter from '../lib/editorRouter';

import getSourceComponent from './source';
import { formatData } from 'editor-utils/dataParser';
import NewEditorPreview from './preview';
import { RecordsChecker } from '../components/recordsChecker.js';

const NewEditor = props => {
  const editorContext = useEditorContext();
  const modalContext = useModalContext();
  const editorRouter = useEditorRouter(props.product.id);

  // Add state to store session data
  const [sessionData, setSessionData] = useState(null);

  const { config } = props;

  const supportedSkus = useMemo(() => {
    return uniq([props.product.id, ...config.alternatives]);
  }, [props.product.id, config]);

  if (props.params[0] === 'preview' && props.item.pages.length > 0) {
    return <NewEditorPreview {...props} />;
  }

  const handleOnPreviewClick = async (generateDataCallback, additionalData = {}) => {
    const loadingModalKey = modalContext.spinner(<>Assembling your creation ...</>, {
      title: 'Building your project',
    });

    const { data, error, session } = await generateDataCallback();

    // Store session data in state
    if (session) {
      setSessionData(session);
    }

    if (error) {
      modalContext.close(loadingModalKey);
      modalContext.confirm(<>{error}</>, {
        title: 'Error creating your project',
        closeable: false,
        confirmLabel: 'OK',
      });
      return;
    }

    // track event
    editorContext.onPreviewPrints(props.product.id);

    let editorItemId = props.item.id;
    // When itemId is same as productId, we need to create new uniq itemId
    if (props.item.id === props.product.id) {
      editorItemId = uuid();
      editorContext.changeItemId(props.item.id, editorItemId);
    }

    editorContext.setPhotos(editorItemId, data.photos);
    editorContext.setPages(editorItemId, data.pages);
    editorContext.setAdditionalInfo(editorItemId, data.additionalInfo);

    // Create new draft
    const formatedData = formatData(props.template, data);
    const { data: syncData } = await syncApi(editorContext.accessToken).saveProductDraft(
      props.product.id,
      props.template.templateUrl,
      props.template.version,
      formatedData,
    );

    editorContext.setDraftName(editorItemId, '');
    if (syncData) {
      editorContext.setDraftId(editorItemId, syncData.id);
      editorContext.setDraftLastModified(editorItemId, syncData.updated_at);
    }

    // in this "new" editor, each editor page has queryString ?id=XYZ
    editorRouter.push('/preview', { id: editorItemId });

    modalContext.close(loadingModalKey);
  };

  const SourceComponent = getSourceComponent(config.source);

  return (
    <DraftList
      item={props.item}
      product={props.product}
      template={props.template}
      productIds={supportedSkus}
    >
      <SourceComponent onSuccess={handleOnPreviewClick} />
      {sessionData && <RecordsChecker editorItemId={props.item.id} sessionId={sessionData.id} accessToken={editorContext.accessToken} />}
    </DraftList>
  );
};

NewEditor.propTypes = {
  id: PropTypes.string.isRequired,
  item: PropTypes.object.isRequired,
  params: PropTypes.array.isRequired,
  config: PropTypes.object.isRequired,
  product: PropTypes.object.isRequired,
  template: PropTypes.object.isRequired,
  onFinish: PropTypes.func.isRequired,
};

export default NewEditor;
